import React, {Component} from "react";
import {Control, Field, Icon} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import SectionName from "../../../elements/SectionName";
import Spacer from "../../../elements/Spacer";
import InputWarning from "../../../elements/InputWarning";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {getFaceRestorationResult} from "../../../api/models/FaceRestoration";

interface IFaceRestorationModelState {
    loading: boolean;
    file: any;
    imageUrl: any;
    imageBase64: any;
    restoredImageBase64: string;
    restoredCroppedFaceBase64: string;
    fileErrorMessage: string;
    errorMessage: string;
    successMessage: string;
}

class FaceRestorationModel extends Component<{}, IFaceRestorationModelState> {
    public ALLOWED_FILE_TYPES: any = ['image/jpeg', 'image/png'];
    public ALLOWED_FILE_TYPES_STR: string = this.ALLOWED_FILE_TYPES.join(',');
    public fileInputRef: any = null;

    public fileInputStyle = {
        width: '100%'
    };

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            restoredImageBase64: "",
            restoredCroppedFaceBase64: "",
            fileErrorMessage: "",
            errorMessage: "",
            successMessage: "",
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.FACE_RESTORATION);
    }

    public handleFileChange = (e: any) => {
        this.setState({
            fileErrorMessage: "",
            errorMessage: "",
            successMessage: "",
            imageBase64: "",
            restoredImageBase64: "",
            restoredCroppedFaceBase64: "",
        });

        const file = e.target.files[0];
        if (file) {
            if (this.ALLOWED_FILE_TYPES.includes(file.type)) {
                const url = URL.createObjectURL(file);
                this.setState({
                    file,
                    imageUrl: url
                }, () => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        this.setState({
                            imageBase64: reader.result
                        });
                    };
                    reader.readAsDataURL(file);
                });
            } else {
                this.setState({
                    file: null,
                    imageUrl: "",
                    imageBase64: "",
                    fileErrorMessage: "The model only accepts JPEG or PNG format files."
                });
            }
        }
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        if (!this.state.file || !this.state.imageBase64 || this.state.fileErrorMessage) {
            if (!this.state.file || !this.state.imageBase64) {
                this.setState({
                    fileErrorMessage: "Please select image."
                });
            }

            return;
        }

        this.setState({
            loading: true,
            errorMessage: "",
            successMessage: ""
        });

        let errorOccurred = false;

        const imageRawBase64 : string = this.state.imageBase64.substring(this.state.imageBase64.indexOf(",") + 1);
        const response = await getFaceRestorationResult(imageRawBase64).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false});
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process image."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data && response.data.result && response.data.cropped_face) {
            this.setState({
                loading: false,
                restoredImageBase64: response.data.result,
                restoredCroppedFaceBase64: response.data.cropped_face
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't process image."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.FACE_RESTORATION);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            file: null,
            imageUrl: "",
            imageBase64: "",
            restoredImageBase64: "",
            restoredCroppedFaceBase64: "",
            fileErrorMessage: "",
            errorMessage: "",
            successMessage: "",
        });

        if (this.fileInputRef) {
            this.fileInputRef.value = "";
        }
    };

    public render() {
        return (
            <React.Fragment>
                <ModelContentContainer>
                    <Card>
                        <Field>
                            <Control>
                                <SectionName>Select Image:</SectionName>
                                <input
                                    ref={(el) => this.fileInputRef = el}
                                    type="file"
                                    accept={this.ALLOWED_FILE_TYPES_STR}
                                    onChange={this.handleFileChange}
                                    style={this.fileInputStyle}
                                />
                                {
                                    this.state.fileErrorMessage ? <div>
                                        <InputWarning>{this.state.fileErrorMessage}</InputWarning>
                                    </div> : <></>
                                }
                                <div style={{
                                                margin: '20px',
                                                textAlign: 'center',
                                                position: 'relative'
                                            }}>
                                    <div>
                                    {
                                        !this.state.fileErrorMessage && this.state.file && this.state.imageUrl ?
                                                <img
                                                    alt="Image"
                                                    src={this.state.imageUrl}
                                                /> : <></>
                                    }
                                    </div>
                                    <div>
                                    {
                                        this.state.restoredImageBase64 ?
                                            <div>
                                                <img
                                                    alt="Result image"
                                                    src={`data:image/png;base64,${this.state.restoredImageBase64}`}
                                                />
                                                <div>
                                                    <a download="restoredImage.png" href={`data:image/png;base64,${this.state.restoredImageBase64}`}>
                                                        <Button style={{marginBottom: "7px", width: "100%"}}>Save</Button>
                                                    </a>
                                                </div>
                                            </div> : <></>
                                    }
                                    </div>
                                    <div>
                                    {
                                        this.state.restoredCroppedFaceBase64 ?
                                            <div>
                                                <img
                                                    alt="Result cropped face image"
                                                    src={`data:image/png;base64,${this.state.restoredCroppedFaceBase64}`}
                                                />
                                                <div>
                                                    <a download="restoredFace.png" href={`data:image/png;base64,${this.state.restoredCroppedFaceBase64}`}>
                                                        <Button style={{marginBottom: "7px", width: "100%"}}>Save</Button>
                                                    </a>
                                                </div>
                                            </div> : <></>
                                    }
                                    </div>
                                </div>
                            </Control>
                        </Field>
                        <Spacer/>
                        <div>
                            <Button onClick={this.onSubmit}>Submit</Button>
                            <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                        </div>
                        {
                            this.state.loading && <div>
                                <Spacer/>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                            </div>
                        }
                        <Spacer/>
                        {
                            !this.state.loading && this.state.errorMessage ?
                                <div>{this.state.errorMessage}</div> : <></>
                        }
                        {
                            !this.state.loading && this.state.successMessage ?
                                <div>{this.state.successMessage}</div> : <></>
                        }
                        <Spacer/>
                    </Card>
                </ModelContentContainer>
            </React.Fragment>
        );
    }
}

export default FaceRestorationModel;



// WEBPACK FOOTER //
// ./src/routes/Dashboard/FaceRestoration/FaceRestorationModel.tsx